import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, gbp } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function ManagerRoster() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ name: "", default_hourly_rate: "" });
  const [editing, setEditing] = useState(null); // {id, name, default_hourly_rate}

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/employees");
      setEmployees(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      const payload = {
        name: form.name.trim(),
        default_hourly_rate: parseFloat(form.default_hourly_rate || "0"),
      };
      if (user.role === "admin") payload.store_id = user.store_id || null;
      await api.post("/employees", payload);
      setForm({ name: "", default_hourly_rate: "" });
      toast.success("Employee added");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/employees/${editing.id}`, {
        name: editing.name.trim(),
        default_hourly_rate: parseFloat(editing.default_hourly_rate || "0"),
      });
      setEditing(null);
      toast.success("Updated");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this employee from your roster?")) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Removed");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1100px]">
      <div className="mb-6">
        <div className="label-uc">Roster</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Employees</h1>
        <p className="text-sm text-zinc-500 mt-1">Maintain the team members at your store and their default hourly rates.</p>
      </div>

      <form onSubmit={add} className="bg-white border border-zinc-200 p-5 mb-6 grid md:grid-cols-[1fr_180px_auto] gap-3 items-end" data-testid="employee-add-form">
        <div>
          <Label className="label-uc">Employee name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="rounded-none border-zinc-300 mt-1.5" required data-testid="employee-name-input" />
        </div>
        <div>
          <Label className="label-uc">Default rate £/hr</Label>
          <Input type="number" step="0.01" min="0" value={form.default_hourly_rate}
            onChange={(e) => setForm({ ...form, default_hourly_rate: e.target.value })}
            className="rounded-none border-zinc-300 mt-1.5 font-mono-data" data-testid="employee-rate-input" />
        </div>
        <Button type="submit" className="rounded-none bg-zinc-950 hover:bg-zinc-800 h-10" data-testid="employee-add-button">
          <Plus className="w-4 h-4 mr-2" /> Add
        </Button>
      </form>

      <div className="bg-white border border-zinc-200">
        <table className="w-full text-sm" data-testid="employees-table">
          <thead className="bg-zinc-50">
            <tr className="text-left">
              <th className="px-4 py-2 label-uc">Name</th>
              <th className="px-4 py-2 label-uc text-right">Default rate</th>
              <th className="px-4 py-2 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 && (
              <tr><td colSpan={3} className="px-4 py-12 text-center text-zinc-400">No employees yet.</td></tr>
            )}
            {employees.map((e) => (
              <tr key={e.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`employee-row-${e.id}`}>
                <td className="px-4 py-2">
                  {editing?.id === e.id ? (
                    <Input value={editing.name} onChange={(ev) => setEditing({ ...editing, name: ev.target.value })}
                      className="rounded-none border-zinc-300 h-8" />
                  ) : e.name}
                </td>
                <td className="px-4 py-2 text-right font-mono-data">
                  {editing?.id === e.id ? (
                    <Input type="number" step="0.01" value={editing.default_hourly_rate}
                      onChange={(ev) => setEditing({ ...editing, default_hourly_rate: ev.target.value })}
                      className="rounded-none border-zinc-300 h-8 text-right font-mono-data" />
                  ) : gbp(e.default_hourly_rate)}
                </td>
                <td className="px-4 py-2 text-right">
                  {editing?.id === e.id ? (
                    <div className="flex gap-2 justify-end">
                      <button onClick={saveEdit} className="text-emerald-600 hover:text-emerald-700" data-testid={`employee-save-${e.id}`}><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditing(null)} className="text-zinc-400 hover:text-zinc-600"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditing({ id: e.id, name: e.name, default_hourly_rate: e.default_hourly_rate })}
                        className="text-zinc-500 hover:text-zinc-900" data-testid={`employee-edit-${e.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(e.id)} className="text-zinc-400 hover:text-[#DF1A22]" data-testid={`employee-delete-${e.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
